/**
 * MessageComposer Component
 * Phase 6: Communication & Engagement
 * Rich message composer with templates and attachments
 */

import { useState, useEffect } from 'react';
import { useMessageTemplates, useFileUpload, useMessagePermission } from '../hooks/useMessaging';
import { messagingService } from '../services/messagingService';
import {
  Send,
  Paperclip,
  FileText,
  X,
  ChevronDown,
  Loader2,
  AlertCircle,
  CheckCircle,
  Info
} from 'lucide-react';

// Template category labels
const CATEGORY_LABELS = {
  introduction: 'Introduction',
  follow_up: 'Follow-up',
  meeting_request: 'Meeting Request',
  deal_update: 'Deal Update'
};

// Template selector dropdown
function TemplateSelector({ templates, onSelect, disabled }) {
  const [isOpen, setIsOpen] = useState(false);

  // Group templates by category
  const grouped = templates.reduce((acc, t) => {
    if (!acc[t.category]) acc[t.category] = [];
    acc[t.category].push(t);
    return acc;
  }, {});

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
      >
        <FileText className="w-4 h-4" />
        Use Template
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute left-0 top-full mt-1 w-72 bg-white rounded-xl shadow-lg border border-gray-200 z-20 max-h-80 overflow-y-auto">
            {Object.entries(grouped).map(([category, categoryTemplates]) => (
              <div key={category} className="py-1">
                <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide bg-gray-50">
                  {CATEGORY_LABELS[category] || category}
                </div>
                {categoryTemplates.map(template => (
                  <button
                    key={template.id}
                    type="button"
                    onClick={() => {
                      onSelect(template);
                      setIsOpen(false);
                    }}
                    className="w-full px-3 py-2 text-left hover:bg-purple-50 transition-colors"
                  >
                    <div className="font-medium text-gray-900 text-sm">{template.name}</div>
                    <div className="text-xs text-gray-500 truncate mt-0.5">
                      {template.subject_template.substring(0, 50)}...
                    </div>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Attachment preview
function AttachmentPreview({ attachment, onRemove }) {
  const fileSize = attachment.size
    ? `${(attachment.size / 1024).toFixed(1)} KB`
    : '';

  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg">
      <FileText className="w-4 h-4 text-gray-500" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-900 truncate">
          {attachment.name}
        </div>
        {fileSize && (
          <div className="text-xs text-gray-500">{fileSize}</div>
        )}
      </div>
      <button
        type="button"
        onClick={onRemove}
        className="p-1 text-gray-400 hover:text-red-500 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

// Permission warning
function PermissionWarning({ permission }) {
  if (permission.allowed) return null;

  return (
    <div className="flex items-start gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
      <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
      <div>
        <p className="font-medium text-yellow-800">Cannot send message</p>
        <p className="text-sm text-yellow-700 mt-1">{permission.reason}</p>
      </div>
    </div>
  );
}

// Main MessageComposer component
export function MessageComposer({
  sellerId,
  buyerId,
  assetId = null,
  buyerName = 'the buyer',
  buyerCompany = '',
  sellerCompany = '',
  assetName = '',
  therapeuticArea = '',
  developmentStage = '',
  target = '',
  modality = '',
  differentiation = '',
  matchScore = 0,
  senderName = '',
  senderType = 'seller',
  onSuccess,
  onCancel,
  className = ''
}) {
  // State
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  // Hooks
  const { templates, loading: loadingTemplates } = useMessageTemplates();
  const { uploadFile, uploading } = useFileUpload();
  const permission = useMessagePermission(sellerId, buyerId);

  // Template placeholder data
  const placeholderData = {
    buyer_name: buyerName,
    buyer_company: buyerCompany,
    seller_company: sellerCompany,
    asset_name: assetName,
    therapeutic_area: therapeuticArea,
    development_stage: developmentStage,
    target: target,
    modality: modality,
    differentiation: differentiation,
    match_score: Math.round(matchScore),
    sender_name: senderName
  };

  // Handle template selection
  const handleTemplateSelect = async (template) => {
    try {
      const filled = await messagingService.fillTemplate(template.id, placeholderData);
      setSubject(filled.subject);
      setBody(filled.body);
    } catch (err) {
      console.error('Error filling template:', err);
      setError('Failed to load template');
    }
  };

  // Handle file upload
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      // For now, just add to attachments list with local data
      // Actual upload will happen on send if needed
      setAttachments(prev => [...prev, {
        name: file.name,
        size: file.size,
        type: file.type,
        file: file // Keep file reference for upload
      }]);
    } catch (err) {
      console.error('Error with file:', err);
      setError(err.message);
    }

    // Reset input
    e.target.value = '';
  };

  // Remove attachment
  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  // Send message
  const handleSend = async () => {
    if (!body.trim()) {
      setError('Please enter a message');
      return;
    }

    if (!permission.allowed) {
      setError(permission.reason);
      return;
    }

    setSending(true);
    setError(null);

    try {
      // Upload attachments first (if any have file references)
      const uploadedAttachments = [];
      for (const att of attachments) {
        if (att.file) {
          // Need to create conversation first to get ID for upload path
          // For simplicity, we'll include file metadata without actual upload
          // In production, you'd handle this differently
          uploadedAttachments.push({
            name: att.name,
            size: att.size,
            type: att.type,
            url: '' // Would be filled after upload
          });
        } else {
          uploadedAttachments.push(att);
        }
      }

      // Send message
      await messagingService.sendNewMessage({
        sellerId,
        buyerId,
        senderType,
        subject: subject || `Message regarding ${assetName || 'partnership opportunity'}`,
        body,
        attachments: uploadedAttachments,
        assetId
      });

      setSuccess(true);

      // Call success callback after short delay
      setTimeout(() => {
        if (onSuccess) onSuccess();
      }, 1500);
    } catch (err) {
      console.error('Error sending message:', err);
      setError(err.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  // Success state
  if (success) {
    return (
      <div className={`bg-white rounded-xl border border-gray-200 p-8 text-center ${className}`}>
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">Message Sent!</h3>
        <p className="text-gray-500 mt-2">
          Your message has been delivered to {buyerName}.
        </p>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl border border-gray-200 ${className}`}>
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">New Message</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              To: {buyerName} {buyerCompany && `at ${buyerCompany}`}
            </p>
          </div>
          {onCancel && (
            <button
              onClick={onCancel}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Permission check */}
      {!permission.loading && !permission.allowed && (
        <div className="px-6 pt-4">
          <PermissionWarning permission={permission} />
        </div>
      )}

      {/* Match score indicator */}
      {matchScore > 0 && (
        <div className="px-6 pt-4">
          <div className="flex items-center gap-2 text-sm">
            <Info className="w-4 h-4 text-blue-500" />
            <span className="text-gray-600">
              Match Score: <span className="font-semibold text-blue-600">{Math.round(matchScore)}%</span>
            </span>
          </div>
        </div>
      )}

      {/* Composer */}
      <div className="p-6 space-y-4">
        {/* Template selector */}
        <div className="flex items-center justify-between">
          <TemplateSelector
            templates={templates}
            onSelect={handleTemplateSelect}
            disabled={loadingTemplates || sending}
          />
        </div>

        {/* Subject */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Subject
          </label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Enter subject..."
            disabled={sending || !permission.allowed}
            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
          />
        </div>

        {/* Body */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Message
          </label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Write your message..."
            rows={10}
            disabled={sending || !permission.allowed}
            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none disabled:bg-gray-50 disabled:text-gray-500"
          />
        </div>

        {/* Attachments */}
        {attachments.length > 0 && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Attachments
            </label>
            <div className="space-y-2">
              {attachments.map((att, index) => (
                <AttachmentPreview
                  key={index}
                  attachment={att}
                  onRemove={() => removeAttachment(index)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="flex items-center gap-2 text-red-600 text-sm">
            <AlertCircle className="w-4 h-4" />
            <span>{error}</span>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
        {/* Attachment button */}
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg cursor-pointer transition-colors">
            <Paperclip className="w-4 h-4" />
            <span>Attach File</span>
            <input
              type="file"
              onChange={handleFileUpload}
              accept=".pdf,.ppt,.pptx,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
              className="hidden"
              disabled={sending || uploading || !permission.allowed}
            />
          </label>
          {uploading && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              Uploading...
            </div>
          )}
        </div>

        {/* Send button */}
        <div className="flex items-center gap-3">
          {onCancel && (
            <button
              onClick={onCancel}
              disabled={sending}
              className="px-4 py-2 text-gray-600 hover:text-gray-900 font-medium rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
          )}
          <button
            onClick={handleSend}
            disabled={sending || !body.trim() || !permission.allowed}
            className="flex items-center gap-2 px-6 py-2 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Send Message
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default MessageComposer;
